import { TextInput, Text, InputWrapper, Select, Box } from '@mantine/core';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import classes from './CreatePatientModal.module.css';
import { FieldItem } from '../../configuration/components/choremeioFields.config';
import { getAnswerOptions } from '@/utils/create-patient-helpers';
import { DateInputWrapper } from '@/components/inputs/DateInputWrapper';
import { BirthDateInput } from '@/components/inputs/BirthDateInput';

// -----------------------------------------------------------------------------
interface RequiredAwareLabelProps {
  label: string;
  required?: boolean;
}

// -----------------------------------------------------------------------------
const RequiredAwareLabel: FC<RequiredAwareLabelProps> = ({
  label,
  required,
}) => {
  return (
    <Text component="span" fw={500}>
      {label}{' '}
      {required && (
        <Text component="span" c="red">
          *
        </Text>
      )}
    </Text>
  );
};

// -----------------------------------------------------------------------------
type Props = {
  field: FieldItem;
  labelKey?: string;
  placeholderAppear?: boolean;
  form: any;
  type?: string;
  className?: string;
};

// -----------------------------------------------------------------------------
export const CreatePatientField = ({
  field,
  labelKey,
  placeholderAppear,
  form,
  className,
}: Props) => {
  const { t } = useTranslation();

  const fieldName = field.fieldName;
  const label =
    labelKey ??
    t(`labels.${fieldName}`, 'Label', { ns: 'create-patient-modal' });
  const placeholder = placeholderAppear
    ? t(`descriptions.enter${fieldName}`, 'Type...', {
        ns: 'create-patient-modal',
      })
    : undefined;

  const commonLabel = (
    <RequiredAwareLabel label={label} required={field.required} />
  );

  // ---------------------------------------------------------------------------
  if (field.fieldType === 'phone') {
    return (
      <InputWrapper
        className={classes.phoneInputWrapper}
        label={commonLabel}
        error={form.errors[fieldName]}
        mb="sm"
      >
        {' '}
        <PhoneInput
          placeholder={placeholder}
          containerClass={classes.phoneInputContainer}
          inputClass={classes.phoneInput}
          buttonClass={classes.phoneButton}
          dropdownClass={classes.phoneDropdown}
          preferredCountries={['gr']}
          country={'gr'}
          disabled={field.disabled}
          {...form.getInputProps(fieldName)}
        />
      </InputWrapper>
    );
  }

  // ---------------------------------------------------------------------------
  if (field.fieldType === 'select') {
    const options = getAnswerOptions(fieldName, field.options || []);
    return (
      <Select
        mb="sm"
        label={commonLabel}
        placeholder={placeholder}
        data={options}
        className={className}
        disabled={field.disabled}
        {...form.getInputProps(fieldName)}
      />
    );
  }
  // ---------------------------------------------------------------------------
  if (field.fieldType === 'date') {
    // Use BirthDateInput for birthdate field
    if (fieldName === 'birthdate') {
      return (
        <Box mb="sm">
          <BirthDateInput
            label={label}
            required={field.required}
            disabled={field.disabled}
            showAge={true}
            showFieldLabels={false}
            size="sm"
            {...form.getInputProps(fieldName)}
          />
        </Box>
      );
    }

    // Use DateInputWrapper for other date fields
    return (
      <DateInputWrapper
        mb="sm"
        label={commonLabel}
        placeholder={placeholder}
        className={className}
        disabled={field.disabled}
        {...form.getInputProps(fieldName)}
      />
    );
  }

  // ---------------------------------------------------------------------------
  return (
    <TextInput
      mb="sm"
      label={commonLabel}
      placeholder={placeholder}
      className={className}
      disabled={field.disabled}
      {...form.getInputProps(fieldName)}
    />
  );
};
